import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShareNetwork, Copy, WhatsappLogo, CalendarDots, ClipboardText, ShoppingCart, Baby } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SHARE_OPTIONS = [
  { key: 'menu', label: 'Ementa semanal', icon: CalendarDots },
  { key: 'plan', label: 'Plano alimentar', icon: ClipboardText },
  { key: 'shopping', label: 'Lista de compras', icon: ShoppingCart },
  { key: 'current_shopping', label: 'Lista de compras', icon: ShoppingCart },
  { key: 'baby_menu', label: 'Ementa do bebé', icon: Baby },
];

export default function ShareButton({ resources, label }) {
  // resources = { menu: menuId, plan: planId, shopping: menuId }
  const [show, setShow] = useState(false);
  const [link, setLink] = useState('');
  const [expiry, setExpiry] = useState('none');
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  const open = () => {
    setShow(true); setLink('');
    // Pre-select available resources
    const init = {};
    Object.keys(resources || {}).forEach(k => { if (resources[k]) init[k] = true; });
    setSelected(init);
  };

  const toggleOption = (key) => {
    if (!resources?.[key]) return;
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const generateLink = async () => {
    const types = Object.keys(selected).filter(k => selected[k] && resources?.[k]);
    if (types.length === 0) { toast.error('Seleciona pelo menos uma opção'); return; }
    setLoading(true);
    try {
      const resourceMap = {};
      types.forEach(t => { resourceMap[t] = resources[t]; });
      const { data } = await axios.post(`${API}/api/share/create`, {
        type: types.length === 1 ? types[0] : 'multi',
        resource_id: types.length === 1 ? resources[types[0]] : '',
        resources: resourceMap,
        expiry,
      });
      setLink(`${window.location.origin}${data.url}`);
    } catch { toast.error('Erro ao gerar link'); }
    finally { setLoading(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(link); toast.success('Link copiado'); };
  const shareWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(`${label || 'Partilha'}:\n${link}`)}`, '_blank'); };

  const availableCount = Object.keys(resources || {}).filter(k => resources[k]).length;

  return (
    <>
      <button onClick={open} className="text-brand-green hover:text-brand-green-hover p-1.5 rounded-lg hover:bg-brand-green/5 transition-colors" title="Partilhar" data-testid="share-btn">
        <ShareNetwork size={16} />
      </button>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Partilhar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!link ? (
              <>
                {/* Content toggles */}
                <div>
                  <p className="text-xs text-brand-muted mb-2">O que queres partilhar?</p>
                  <div className="space-y-2">
                    {SHARE_OPTIONS.map(opt => {
                      const available = !!resources?.[opt.key];
                      const checked = !!selected[opt.key];
                      return (
                        <button key={opt.key} onClick={() => toggleOption(opt.key)} disabled={!available}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${!available ? 'opacity-30 cursor-not-allowed border-brand-border' : checked ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`}
                          data-testid={`share-toggle-${opt.key}`}>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${checked ? 'bg-brand-green border-brand-green' : 'border-brand-border'}`}>
                            {checked && <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none" /></svg>}
                          </div>
                          <opt.icon size={18} className={checked ? 'text-brand-green' : 'text-brand-muted'} weight="duotone" />
                          <span className={`text-sm ${checked ? 'text-brand-text font-medium' : 'text-brand-muted'}`}>{opt.label}</span>
                          {!available && <span className="text-[9px] text-brand-placeholder ml-auto">Não disponível</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Expiry */}
                <div>
                  <p className="text-xs text-brand-muted mb-2">Validade do link:</p>
                  <div className="flex gap-2">
                    {[{ v: '7d', l: '1 semana' }, { v: '30d', l: '1 mês' }, { v: 'none', l: 'Sem limite' }].map(o => (
                      <button key={o.v} onClick={() => setExpiry(o.v)} className={`flex-1 py-2 rounded-xl text-xs font-medium ${expiry === o.v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <button onClick={generateLink} disabled={loading || Object.values(selected).filter(Boolean).length === 0} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50">
                  {loading ? 'A gerar...' : 'Gerar link de partilha'}
                </button>
              </>
            ) : (
              <>
                <div className="bg-brand-bg rounded-xl p-3">
                  <p className="text-xs text-brand-muted mb-1">Link gerado:</p>
                  <p className="text-sm text-brand-text break-all">{link}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={copyLink} className="flex-1 border border-brand-border text-brand-text rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-bg">
                    <Copy size={16} /> Copiar
                  </button>
                  <button onClick={shareWhatsApp} className="flex-1 bg-[#25D366] text-white rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2">
                    <WhatsappLogo size={16} weight="fill" /> WhatsApp
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
